<template>
  <div class="reply-node" :class="{ 'mb-4': !grouped }">
    <PostItem 
      :post="reply.post" 
      :grouped="grouped"
      is-reply
      @reply="$emit('reply', $event)"
      @view-thread="$emit('view-thread', $event)"
      @delete="$emit('delete', $event)"
      @view-profile="$emit('view-profile', $event)"
      @quote="$emit('quote', $event)"
      @tag-click="$emit('tag-click', $event)"
    >
      <!-- Grouped Chain: 
           1. If we are in the Official Author Chain, only group if the next post is also by the author.
           2. If we are in the Community section, group the first child to form a conversation block.
      -->
      <template v-if="reply.replies && reply.replies.length > 0">
        <ThreadReplyItem
          v-if="isAuthorChain ? (reply.replies[0].post.author.handle === authorHandle) : true"
          :reply="reply.replies[0]"
          grouped
          :is-author-chain="isAuthorChain"
          :author-handle="authorHandle"
          :root="root"
          :replying-to-uri="replyingToUri"
          @reply="$emit('reply', $event)"
          @view-thread="$emit('view-thread', $event)"
          @delete="$emit('delete', $event)"
          @view-profile="$emit('view-profile', $event)"
          @quote="$emit('quote', $event)"
          @tag-click="$emit('tag-click', $event)"
          @reply-success="$emit('reply-success')"
          @reply-cancel="$emit('reply-cancel')"
        />
      </template>
    </PostItem>
    
    <div v-if="replyingToUri === reply.post.uri" class="reply-input-wrapper mb-4 ml-5">
      <ReplyInput 
        :root="root"
        :parent="{ uri: reply.post.uri, cid: reply.post.cid }"
        @success="$emit('reply-success')"
        @cancel="$emit('reply-cancel')"
      />
    </div>

    <!-- Separate Blocks: Subsequent branches of a post. 
         These start NEW cards (grouped=false).
    -->
    <div v-if="!grouped" class="reply-branches">
      <template v-for="(childReply, idx) in reply.replies" :key="childReply.post.uri">
        <!-- If we are in AuthorChain, ALL branches (including idx 0 if it's not author) 
             are handled by ThreadView. So we HIDE them here to prevent duplicates.
             If we are NOT in AuthorChain (Community section), we render branches (idx > 0)
             as separate blocks.
        -->
        <ThreadReplyItem
          v-if="isAuthorChain ? false : (Number(idx) > 0)"
          :reply="childReply"
          :grouped="false"
          :is-author-chain="false"
          :author-handle="childReply.post.author.handle"
          :root="root"
          :replying-to-uri="replyingToUri"
          @reply="$emit('reply', $event)"
          @view-thread="$emit('view-thread', $event)"
          @delete="$emit('delete', $event)"
          @view-profile="$emit('view-profile', $event)"
          @quote="$emit('quote', $event)"
          @tag-click="$emit('tag-click', $event)"
          @reply-success="$emit('reply-success')"
          @reply-cancel="$emit('reply-cancel')"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import PostItem from './PostItem.vue';
import ReplyInput from './ReplyInput.vue';

defineProps<{
  reply: any;
  root: { uri: string; cid: string };
  replyingToUri: string;
  grouped?: boolean;
  isAuthorChain?: boolean;
  authorHandle?: string;
}>();

defineEmits(['reply', 'view-thread', 'delete', 'view-profile', 'reply-success', 'reply-cancel', 'quote', 'tag-click']);
</script>

<style scoped>
.nested-replies {
  margin-left: 0;
  border-left: none;
}

.reply-node {
  position: relative;
}

.reply-input-wrapper {
  animation: expand 0.3s ease-out;
}

@keyframes expand {
  from { opacity: 0; max-height: 0; overflow: hidden; }
  to { opacity: 1; max-height: 500px; }
}
</style>
